import { Dropdown, Input, DropdownItem } from '@distribusion/owl-frontend-ui'
import cn from 'classnames'
import React, { FocusEvent, UIEvent, ReactElement, useEffect, useMemo, useState } from 'react'

import discountUtils from '@lib/discount'
import { required } from '@lib/validators'

interface DiscountSelectProps {
  availableCards: DiscountCard.Item[]
  label: string
  onChange: (card: DiscountCode.Card | null) => void
  setError: (error: string | undefined) => void
  error: string | undefined | null
  fullWidth?: boolean
  value?: DiscountCode.Card
  disabled?: boolean
}

interface InitialCardValues {
  name: DiscountCode.Name | null
  code: string
}

const cardToOption = ({ name, marketingName }: DiscountCard.Item): DropdownItem<DiscountCode.Name> => ({
  value: name,
  label: marketingName,
})

const findCard = (cards: DiscountCard.Item[], name?: DiscountCode.Name): DiscountCard.Item | undefined =>
  cards?.find(card => card.name === name)

const getCardValue = (list: DropdownItem<string>[], card?: DiscountCode.Card): InitialCardValues => {
  if (!card) return { name: null, code: '' }
  if (!list.some(item => item.value === card.name)) return { name: null, code: '' }

  return { name: card.name, code: card.code ?? '' }
}

const DiscountSelect = (props: DiscountSelectProps): ReactElement => {
  const { availableCards, setError, onChange, error, fullWidth, disabled, label, value } = props
  const options = useMemo(() => availableCards?.map(cardToOption), [availableCards])
  const classNames = cn(fullWidth ? 'cell-12' : 'cell-6 cell-sm-12')

  const [selectedCardName, setSelectedCardName] = useState<DiscountCode.Name | null>(
    () => getCardValue(options, value).name,
  )
  const [selectedCardCode, setSelectedCardCode] = useState<string>(() => getCardValue(options, value).code)
  const [selectedCard, setSelectedCard] = useState<DiscountCard.Item | null | undefined>(() =>
    findCard(availableCards, value?.name),
  )
  const [errorVisible, setErrorVisible] = useState<boolean>(false)

  // update value, when updated from outside
  useEffect(() => {
    setSelectedCardName(getCardValue(options, value).name)
    setSelectedCardCode(getCardValue(options, value).code)
  }, [options, value])
  useEffect(() => setSelectedCard(findCard(availableCards, value?.name)), [availableCards, value])

  const updateCard = (card: DiscountCard.Item | undefined, code: string): void => {
    /* istanbul ignore next: Only a bug or tampering would make it so this is called without a card */
    if (!card) {
      onChange(null)
      return
    }

    if (card.requiresCode) {
      onChange(discountUtils.getCodeCard(card, code))
    } else {
      setSelectedCardCode('')
      onChange(discountUtils.getCodeCard(card))
    }
  }

  const onCardDataChange = (name: DiscountCode.Name, code: string): void => {
    const card = findCard(availableCards, name)
    const error = card?.requiresCode ? required(selectedCardCode) : undefined
    setSelectedCard(card)
    setError(error)

    if (!error) updateCard(card, code)
  }

  const handleReset = (event?: UIEvent): void => {
    event && event.stopPropagation()
    setSelectedCardCode('')
    setSelectedCardName(null)
    setSelectedCard(null)
    setError(undefined)
    onChange(null)
  }
  const handleCodeBlur = (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    setErrorVisible(true)
    onCardDataChange(selectedCardName as DiscountCode.Name, e.target.value)
  }
  const handleNameChange = (name: DiscountCode.Name, event?: UIEvent): void => {
    event && event.stopPropagation()
    setSelectedCardName(name)
    onCardDataChange(name, selectedCardCode)
  }

  return (
    <>
      <div className={classNames}>
        <Dropdown
          items={options}
          label={label}
          value={selectedCardName}
          onChange={(item, event) => handleNameChange(item, event)}
          onReset={handleReset}
          resettable
          disabled={disabled}
        />
      </div>
      {selectedCard?.requiresCode && (
        <div className={classNames}>
          <Input
            label={selectedCard.description}
            required
            value={selectedCardCode}
            onChange={setSelectedCardCode}
            onBlur={handleCodeBlur}
            errorMessage={errorVisible ? error : null}
            disabled={disabled}
            ariaLabel={selectedCard.description}
          />
        </div>
      )}
    </>
  )
}

export default DiscountSelect
